import React, { ReactNode, useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { ModernBottomNav } from '@/components/navigation/ModernBottomNav'
import { ModernTopBar } from '@/components/layout/ModernTopBar'
import { usePWAOptimizations } from '@/hooks/usePWAOptimizations'
import { useUiStore } from '@/stores/uiStore'
import { cn } from '@/lib/utils'

interface ModernLayoutProps {
  children: ReactNode
  showBottomNav?: boolean
  showTopBar?: boolean
  className?: string
}

export function ModernLayout({
  children,
  showBottomNav = true,
  showTopBar = true,
  className
}: ModernLayoutProps) {
  const location = useLocation()
  const [isTransitioning, setIsTransitioning] = useState(false)

  // Use PWA optimization hook
  const { isPWA, isMobile } = usePWAOptimizations()

  // Get mobile chat overlay state from UI store
  const { isMobileChatOverlayOpen } = useUiStore()

  // Pages that should hide navigation (always hide on these pages)
  const alwaysHideNavPages = ['/auth', '/login', '/register', '/swipe', '/dating/swipe']

  // Check for mobile chat conversations (hide nav on mobile when in a specific chat)
  const isInChatConversation = isMobile && /\/chat\/\w+/.test(location.pathname)

  // Detect chat routes for layout-specific handling
  const isChatRoute = location.pathname === '/chat' || location.pathname.startsWith('/chat/')

  // Combined logic for hiding navigation
  const shouldHideNav = alwaysHideNavPages.some(page => location.pathname.startsWith(page)) ||
                        isInChatConversation ||
                        isMobileChatOverlayOpen

  // Debug logging
  console.log('ModernLayout nav visibility:', {
    pathname: location.pathname,
    isMobile,
    showBottomNav,
    isInChatConversation,
    isMobileChatOverlayOpen,
    shouldHideNav,
    willShowNav: showBottomNav && !shouldHideNav
  })

  // Enhanced page transition configuration
  const pageVariants = {
    initial: { 
      opacity: 0,
      y: 8,
      scale: 0.996,
    },
    animate: { 
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.15,
        ease: [0.25, 0.46, 0.45, 0.94], // Custom easing
        opacity: { duration: 0.1 },
        scale: { duration: 0.2 },
        y: { duration: 0.15 }
      }
    },
    exit: { 
      opacity: 0,
      y: -4,
      scale: 1.002,
      transition: {
        duration: 0.1,
        ease: [0.25, 0.46, 0.45, 0.94],
        opacity: { duration: 0.08 }
      }
    }
  }

  // Handle transition state for preventing flicker
  const handleAnimationStart = () => setIsTransitioning(true)
  const handleAnimationComplete = () => setIsTransitioning(false)

  return (
    <div className={cn(
      "h-screen flex flex-col bg-gradient-to-br from-dark-950 via-dark-900 to-dark-950",
      "smooth-scroll",
      isPWA && "pwa-optimized",
      isTransitioning && "transitioning"
    )}>
      {/* Background Pattern - Optimized for performance */}
      <div className="fixed inset-0 opacity-10 pointer-events-none paint-containment">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-kinky-600/20 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-passion-600/20 via-transparent to-transparent" />
      </div>

      {/* Top Bar */}
      {showTopBar && !shouldHideNav && (
        <ModernTopBar />
      )}

      {/* Spacer for fixed top bar */}
      {showTopBar && !shouldHideNav && (
        <div className="h-16 flex-shrink-0" />
      )}

      {/* Main Content - flex-1 to fill remaining space */}
      <main
        className={cn(
          "flex-1 min-h-0",
          // On chat routes, disable main scrolling to avoid double scroll; the chat page manages its own scroll
          isChatRoute ? "overflow-hidden" : "overflow-y-auto",
          className
        )}
      >
          <AnimatePresence
            mode="wait"
            initial={false}
            onExitComplete={() => {
              // Force repaint to prevent black screen
              window.requestAnimationFrame(() => {
                document.body.style.transform = 'translateZ(0)'
                setTimeout(() => {
                  document.body.style.transform = ''
                }, 0)
              })
            }}
          >
            <motion.div
              key={location.pathname}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              onAnimationStart={handleAnimationStart}
              onAnimationComplete={handleAnimationComplete}
              className={cn(
                isChatRoute ? "h-full" : "min-h-full",
                "w-full",
                "performance-critical",
                "framer-motion"
              )}
              style={{
                // Prevent black screen during transitions
                backgroundColor: 'transparent',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'translate3d(0, 0, 0)',
                willChange: isTransitioning ? 'transform, opacity' : 'auto'
              }}
            >
              <div className={cn(
                "critical-content flex flex-col",
                isChatRoute ? "h-full" : "min-h-full",
                isPWA && "touch-optimized no-select"
              )}>
                {children}
              </div>
            </motion.div>
          </AnimatePresence>
        </main>

      {/* Spacer for fixed bottom nav */}
      {showBottomNav && !shouldHideNav && (
        <div className="h-16 flex-shrink-0" />
      )}

      {/* Bottom Navigation */}
      {showBottomNav && !shouldHideNav && <ModernBottomNav />}
    </div>
  )
}